import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  TextField,
  Button,
  Grid,
  MenuItem,
  Box,
  FormHelperText,
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import clsx from 'clsx';
import dayjs from 'dayjs';

import { useTeam } from '../../context/TeamContext';
import { userSchema } from './UserSchema';
import { transformUserToForm } from './helper';

const UserForm = ({ onCancel, onSubmit, user, isViewOnly = false }) => {
  const { teams } = useTeam();
  const {
    control,
    formState: { errors },
    setValue,
    clearErrors,
    handleSubmit,
  } = useForm({
    resolver: zodResolver(userSchema),
    defaultValues: user
      ? transformUserToForm(user)
      : {
          first_name: '',
          last_name: '',
          email: '',
          sex: '',
          team: '',
          dob: '',
          start_date: '',
          phone: '',
        },
    disabled: isViewOnly,
  });

  const renderDatePicker = (field, label) => {
    return (
      <DatePicker
        {...field}
        className={clsx('full-width', 'date-picker', {
          'Mui-error': !!errors[field.name],
        })}
        onChange={(val) => {
          setValue(field.name, val.format('YYYY-MM-DD'));
          clearErrors(field.name);
        }}
        value={field.value ? dayjs(field.value, 'YYYY-MM-DD') : null}
        label={label}
        renderInput={(params) => <TextField {...params} variant="outlined" />}
      />
    );
  };

  const renderInput = (name, label) => (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <TextField
          {...field}
          label={label}
          variant="outlined"
          fullWidth
          error={!!errors[name]}
          helperText={errors[name] ? errors[name].message : ''}
        />
      )}
    />
  );

  const renderDateInput = (name, label) => (
    <Grid item xs={12} sm={6}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Controller
          name={name}
          control={control}
          render={({ field }) => renderDatePicker(field, label)}
        />
      </LocalizationProvider>
      {!!errors[name] && (
        <FormHelperText sx={{ marginLeft: '14px' }} error>
          {errors[name].message}
        </FormHelperText>
      )}
    </Grid>
  );

  const handleFormSubmit = (data) => {
    onSubmit(data);
  };

  return (
    <Box mt={2}>
      <form onSubmit={handleSubmit(handleFormSubmit)} noValidate>
        <Grid container spacing={2}>
          {/* First Name */}
          <Grid item xs={12} sm={6}>
            {renderInput('first_name', 'First Name')}
          </Grid>

          {/* Last Name */}
          <Grid item xs={12} sm={6}>
            {renderInput('last_name', 'Last Name')}
          </Grid>

          {/* Email */}
          <Grid item xs={12}>
            {renderInput('email', 'Email')}
          </Grid>

          {/* Sex */}
          <Grid item xs={12}>
            <Controller
              name="sex"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  label="Sex"
                  variant="outlined"
                  fullWidth
                  error={!!errors.sex}
                  helperText={errors.sex ? errors.sex.message : ''}>
                  <MenuItem value="Male">Male</MenuItem>
                  <MenuItem value="Female">Female</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </TextField>
              )}
            />
          </Grid>

          {/* Date of Birth */}
          {renderDateInput('dob', 'Date of Birth')}

          {/* Joined Date */}
          {renderDateInput('start_date', 'Start Date')}

          {/* Phone Number */}
          <Grid item xs={12}>
            {renderInput('phone', 'Phone Number')}
          </Grid>

          <Grid item xs={12}>
            <Controller
              name="team"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  label="Team"
                  variant="outlined"
                  fullWidth
                  error={!!errors.team}
                  helperText={errors.team ? errors.team.message : ''}>
                  {teams.map((team) => {
                    const teamId = team.id || team._id;
                    if (teamId === 'all') return null;
                    return <MenuItem key={teamId} value={teamId}>{team.name}</MenuItem>;
                  })}
                </TextField>
              )}
            />
          </Grid>

          {/* Submit Button */}
          <Grid item xs={12}>
            <Box
              sx={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <Button onClick={onCancel} color="secondary" variant="outlined">
                {isViewOnly ? 'Close' : 'Cancel'}
              </Button>
              {!isViewOnly && (
                <Button
                  onClick={handleSubmit}
                  type="submit"
                  color="primary"
                  variant="contained">
                  {user ? 'Save' : 'Add Member'}
                </Button>
              )}
            </Box>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};

export default UserForm;
